'use client';

import React, { useEffect, useRef, useState } from 'react';

interface Metric {
  label: string;
  value: number;
  suffix: string;
  prefix?: string;
  tag: string;
}

const METRICS: Metric[] = [
  { label: 'Active Threats Tracked', value: 1847, suffix: '', tag: '[LIVE]' },
  { label: 'Syndicates Identified', value: 312, suffix: '', tag: '[CONFIRMED]' },
  { label: 'Assets Traced', value: 94, suffix: 'M', prefix: '$', tag: '[USD]' },
  { label: 'Operations Closed', value: 2641, suffix: '', tag: '[RESOLVED]' },
  { label: 'Jurisdictions Active', value: 47, suffix: '', tag: '[GLOBAL]' },
];

function CounterValue({ target, suffix, prefix }: { target: number; suffix: string; prefix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 1800;
          const startTime = performance.now();
          const animate = (now: number) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * target));
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [target]);

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
}

export default function MetricsTicker() {
  return (
    <section
      className="border-y border-border bg-card relative overflow-hidden"
      aria-label="ForensicDesk operational metrics"
    >
      {/* Scan line */}
      <div className="absolute top-0 left-0 w-full h-px border-scan" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-2 h-2 rounded-full bg-accent threat-pulse" />
          <span className="bracket-tag text-muted-foreground">THREAT RADAR // REAL-TIME OPERATIONAL METRICS</span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8">
          {METRICS.map((m) => (
            <div key={m.label} className="flex flex-col gap-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="bracket-tag text-accent">{m.tag}</span>
              </div>
              <div className="text-2xl md:text-3xl font-black tracking-tighter text-foreground terminal-text">
                <CounterValue target={m.value} suffix={m.suffix} prefix={m.prefix} />
              </div>
              <div className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                {m.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}